import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Loader2, CheckCircle2, Instagram } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { EVENT, OWNER_EMAIL, OWNER_PHONE, OWNER_PHONE_DISPLAY, LINKS } from "../lib/site";

const field = "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink-soft/60 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact" path="/contact" description="Get in touch with the Grimsby On The Lake Turkey Trot team." />
      <PageHeader
        chapter="say hello"
        title="Let's talk turkey."
        sub="Questions, sponsorship enquiries or just want to cheer us on? Drop us a line and we'll get right back to you."
      />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 pb-24 sm:pb-32 grid gap-10 lg:grid-cols-5 items-start">
        <Reveal className="lg:col-span-2 space-y-5">
          <div className="rounded-3xl bg-white border border-black/5 shadow-warm p-8">
            <p className="flex items-start gap-3"><MapPin size={20} className="text-terracotta mt-0.5" />
              <span>{EVENT.location.name}<br /><span className="text-ink-soft text-sm">{EVENT.location.address}</span></span>
            </p>
            <p className="mt-5 flex items-center gap-3"><Mail size={20} className="text-terracotta" />
              <a href={`mailto:${OWNER_EMAIL}`} className="hover:text-terracotta transition-colors duration-200" data-testid="contact-email-link">{OWNER_EMAIL}</a>
            </p>
            <p className="mt-4 flex items-center gap-3"><Phone size={20} className="text-terracotta" />
              <a href={`tel:${OWNER_PHONE}`} className="hover:text-terracotta transition-colors duration-200" data-testid="contact-phone-link">{OWNER_PHONE_DISPLAY}</a>
            </p>
            <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-3 group" data-testid="contact-instagram-link">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-cream-200 group-hover:bg-terracotta group-hover:text-white transition-colors duration-200"><Instagram size={18} /></span>
              <span className="text-sm font-semibold group-hover:text-terracotta transition-colors duration-200">@grimsbyturkeytrot</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          {done ? (
            <div className="rounded-3xl bg-white border border-black/5 shadow-warm p-10 text-center" data-testid="contact-success">
              <CheckCircle2 size={44} className="mx-auto text-forest" />
              <h3 className="mt-4 font-display text-2xl font-semibold">Message sent!</h3>
              <p className="mt-2 text-ink-soft">Thanks for reaching out, we'll reply as soon as we can.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white border border-black/5 shadow-warm p-8 sm:p-10 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Your name" value={form.name} onChange={set("name")} className={field} data-testid="contact-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="contact-email" />
              </div>
              <input required placeholder="Subject" value={form.subject} onChange={set("subject")} className={field} data-testid="contact-subject" />
              <textarea required placeholder="Your message" rows={6} value={form.message} onChange={set("message")} className={field} data-testid="contact-message" />
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-sienna transition-colors duration-200 disabled:opacity-60" data-testid="contact-submit">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send message"}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </>
  );
}
